import { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { formatPercent } from "@/components/pricing/pricingFormat";
import { useFreeEntryPlan } from "@/lib/usePlanFacts";

// KAN-1955 — the sample calculator (configurator form, 2D/3D preview and
// its API calls) made the landing take 5-9 s to settle. It now loads in
// its own chunk, and only once the browser is idle after the first
// paint, so the headline and buttons show without waiting for it.
const PlatformDemoCard = lazy(() =>
  import("./PlatformDemoCard").then((m) => ({ default: m.PlatformDemoCard })),
);

function useAfterFirstPaint(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const done = () => setReady(true);
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(done, { timeout: 1500 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(done, 200);
    return () => window.clearTimeout(id);
  }, []);
  return ready;
}

const TRUST_KEYS = ["fast", "czech"] as const;

/**
 * The terms under the main button (KAN-1944).
 *
 * "Try it free" used to stand next to "no monthly fee" while the card check
 * and the commission sat in the closing block and the FAQ — a shop owner
 * read it as "free, with a catch". One sentence now says all three, and
 * the rate is the free tier's ``platform_fee_bps`` from ``/public/plans``,
 * the number the pricing page prints. Until that answers (or when no tier
 * is free) the sentence names no number at all.
 */
function useHeroTerms(lang: string): string {
  const { t } = useTranslation("common");
  const free = useFreeEntryPlan();
  if (!free) return t("platformLanding.hero.termsFallback");
  if (free.platform_fee_bps <= 0) return t("platformLanding.hero.termsNoFee");
  return t("platformLanding.hero.terms", {
    percent: formatPercent(free.platform_fee_bps, lang),
  });
}

export function PlatformHero({ lang }: { lang: string }): JSX.Element {
  const { t } = useTranslation("common");
  const terms = useHeroTerms(lang);
  const demoReady = useAfterFirstPaint();
  const demoPlaceholder = (
    <div className="demo" data-testid="platform-demo-pending">
      <p className="p-5">{t("platformSample.loading")}</p>
    </div>
  );

  return (
    <section className="hero" data-testid="platform-landing-hero">
      <div aria-hidden="true" className="hero-grid grid-bg" />
      <div className="hero-inner">
        <div className="hero-text">
          <div className="eyebrow">
            <span aria-hidden="true" className="dot" />
            <span>{t("platformLanding.hero.eyebrow")}</span>
          </div>
          <h1 data-testid="platform-hero-heading" className="hero-h1">
            {t("platformLanding.hero.titlePre")}{" "}
            <span className="gray">
              {t("platformLanding.hero.titleEmphasis")}
            </span>{" "}
            {t("platformLanding.hero.titlePost")}
          </h1>
          <p className="hero-sub">{t("platformLanding.hero.subtitle")}</p>

          <div className="cta-row">
            <Link
              to={`/${lang}/sign-up-shop`}
              data-testid="platform-cta-signup"
              className="btn-cta"
            >
              {t("platformLanding.hero.ctaPrimary")}
              <span aria-hidden="true">→</span>
            </Link>
            <Link
              to={`/${lang}/try`}
              data-testid="platform-cta-tryout"
              className="btn-cta-2"
            >
              {t("platformLanding.hero.ctaSecondary")}
            </Link>
          </div>
          <p className="hero-terms" data-testid="platform-hero-terms">
            {terms}
          </p>

          <ul className="hero-trust">
            {TRUST_KEYS.map((key) => (
              <li key={key}>
                <svg
                  className="check"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {t(`platformLanding.hero.trust.${key}`)}
              </li>
            ))}
          </ul>
        </div>

        <div className="hero-demo">
          {demoReady ? (
            <Suspense fallback={demoPlaceholder}>
              <PlatformDemoCard />
            </Suspense>
          ) : (
            demoPlaceholder
          )}
        </div>
      </div>
    </section>
  );
}
